import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import Breadcrumbs from "src/components/Common/Breadcrumb";
import RichTable, { RichColumn } from "src/components/Page/RichTable";
import ColumnButton from "src/components/Page/GridButtons/ColumnButton";
import { useAppDispatch } from "src/store";
import { GetLocaleDate } from "src/helpers/getLocaleDate";
import {
  deleteSubscription,
  getSubscriptions,
  getSubscriptionStats,
} from "src/store/subscriptions/saga";
import { SubscriptionDto, SubscriptionStatsDto } from "src/store/subscriptions/type";
import CampaignForm from "./campaign";
import SubscriberAddForm from "./add";
import { onSuccessClickFormBasicForm } from "src/helpers/onSuccess";

const STATUS: Record<number, { label: string; cls: string }> = {
  1: { label: "Bekliyor", cls: "badge-soft-warning" },
  2: { label: "Abone", cls: "badge-soft-success" },
  3: { label: "Çıktı", cls: "badge-soft-danger" },
  4: { label: "Bounce", cls: "badge-soft-secondary" },
};

const SubscriptionsPage = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [data, setData] = useState<SubscriptionDto[]>([]);
  const [stats, setStats] = useState<SubscriptionStatsDto>();
  const [loading, setLoading] = useState(true);
  const [showCampaign, setShowCampaign] = useState(false);
  const [showAdd, setShowAdd] = useState(false);

  document.title = t("Subscriptions.PageTitle");

  const fetchData = () => {
    setLoading(true);
    dispatch(
      getSubscriptions({
        params: "",
        payload: {
          onSuccess: (_m: any, p: SubscriptionDto[]) => setData(Array.isArray(p) ? p : []),
          onError: () => {},
          onFinally: () => setLoading(false),
        },
      })
    );
    dispatch(
      getSubscriptionStats({
        payload: { onSuccess: (_m: any, p: SubscriptionStatsDto) => setStats(p), onError: () => {} },
      })
    );
  };
  useEffect(() => { fetchData(); }, []);

  const exportCsv = () => {
    const cols = ["Email", "Name", "Type", "Locale", "Source", "Status", "CreateTime"];
    const esc = (v: any) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [cols.join(",")].concat(
      data.map((r) =>
        [
          r.Email,
          r.Name,
          r.IsMember ? "Uye" : "Ziyaretci",
          r.Locale,
          r.Source,
          (STATUS[r.StatusId] || {}).label,
          r.CreateTime,
        ]
          .map(esc)
          .join(",")
      )
    );
    const blob = new Blob(["﻿" + lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `aboneler-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const columns: RichColumn<SubscriptionDto>[] = [
    { key: "Email", label: t("Subscriptions.Email") },
    { key: "Name", label: t("Subscriptions.Name") },
    {
      key: "IsMember",
      label: t("Subscriptions.Type"),
      value: (r) => (r.IsMember ? 1 : 0),
      render: (r) =>
        r.IsMember ? (
          <span className="badge badge-soft-primary">{t("Subscriptions.Member")}</span>
        ) : (
          <span className="badge badge-soft-info">{t("Subscriptions.Visitor")}</span>
        ),
    },
    { key: "Locale", label: t("Subscriptions.Locale") },
    { key: "Source", label: t("Subscriptions.Source") },
    {
      key: "StatusId",
      label: t("Subscriptions.Status"),
      value: (r) => r.StatusId,
      render: (r) => {
        const s = STATUS[r.StatusId] || { label: String(r.StatusId), cls: "badge-soft-secondary" };
        return <span className={"badge " + s.cls}>{s.label}</span>;
      },
    },
    { key: "CreateTime", label: t("Subscriptions.CreateTime"), render: (r) => GetLocaleDate((r as any).CreateTime) },
  ];

  const statCards: { label: string; value: number | undefined; color: string }[] = [
    { label: t("Subscriptions.StatTotal"), value: stats?.total, color: "primary" },
    { label: t("Subscriptions.StatSubscribed"), value: stats?.subscribed, color: "success" },
    { label: t("Subscriptions.StatMembers"), value: stats?.members, color: "info" },
    { label: t("Subscriptions.StatPending"), value: stats?.pending, color: "warning" },
    { label: t("Subscriptions.StatUnsub"), value: stats?.unsubscribed, color: "danger" },
    { label: t("Subscriptions.StatMonth"), value: stats?.thisMonth, color: "dark" },
  ];

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <div className="d-flex flex-wrap align-items-center justify-content-between">
            <Breadcrumbs title={t("Breadcrumb.Dashboard")} breadcrumbItem={t("Subscriptions.PageTitle")} />
            <div className="d-flex gap-2 mb-3">
              <button className="btn btn-warning" onClick={fetchData} title={t("Components.Refresh", "Yenile") as string}>
                <i className="mdi mdi-refresh" />
              </button>
              <button className="btn btn-light" onClick={exportCsv}>
                <i className="bx bx-download" /> {t("Subscriptions.ExportCsv")}
              </button>
              <button className="btn btn-success text-light" onClick={() => setShowAdd(true)}>
                <i className="bx bx-plus" /> {t("Subscriptions.AddButton")}
              </button>
              <button className="btn btn-primary" onClick={() => setShowCampaign(true)}>
                <i className="bx bx-send" /> {t("Subscriptions.SendCampaign")}
              </button>
            </div>
          </div>

          <Row>
            {statCards.map((s, i) => (
              <Col key={i} md={2} sm={4} xs={6}>
                <Card>
                  <CardBody className="py-3">
                    <div className={"text-" + s.color} style={{ fontSize: 22, fontWeight: 700 }}>
                      {s.value ?? "—"}
                    </div>
                    <div className="text-muted" style={{ fontSize: 12 }}>{s.label}</div>
                  </CardBody>
                </Card>
              </Col>
            ))}
          </Row>

          <RichTable<SubscriptionDto>
            tableId="subscriptions"
            t={t as any}
            columns={columns}
            rows={data}
            loading={loading}
            rowKey={(r) => (r as any).Id}
            emptyText={t("Subscriptions.Empty", "Abone bulunamadı") as string}
            actions={(r) => (
              <ColumnButton
                buttonType="delete"
                onClick={() =>
                  dispatch(
                    deleteSubscription({
                      id: (r as any).Id,
                      payload: { onSuccess: () => fetchData(), onError: () => {} },
                    })
                  )
                }
              />
            )}
          />
        </Container>
      </div>

      <SubscriberAddForm
        open={showAdd}
        onHide={() => setShowAdd(false)}
        onSuccessClick={(m) =>
          onSuccessClickFormBasicForm({ dispatch, message: m, action: getSubscriptions, setDataList: setData })
        }
      />
      <CampaignForm open={showCampaign} onHide={() => setShowCampaign(false)} onSuccessClick={() => fetchData()} />
    </React.Fragment>
  );
};

export default SubscriptionsPage;
